import React, { useEffect } from 'react';
import { Button, CircularProgress, TextField } from '@iclinic/design-system';
import { Form, Formik, useFormikContext } from 'formik';
import { useRifm } from 'rifm';
import ReactInputMask from 'react-input-mask';
import { TextFieldProps } from '@iclinic/design-system/src/components/TextField';
import { useDispatch, useSelector } from 'react-redux';

import {
  cepFormatter,
  cnpjFormatter,
  cpfFormatter,
} from 'shared/utils/formatters';
import useStyles from './dialogStyles.style';
import { close, submitClinicBilling } from '../state';
import { fetchZipCode } from 'features/checkout/services/getZipCodeAddress';
import { ClinicBillingType } from '../types';
import * as S from './ClinicBillingForm.styles';
import {
  clinicBillingFormSelector,
  requestStatusDerivedSelector,
} from '../state/selectors';
import { validationClinicBillingForm } from '../validation';
import { useGetZipCodeAddress } from './hooks';
import ErrorMessage from './ErrorMessage';
import { isoToBrDate } from '../utils';
import { BillingAddressFields } from './BillingAddressFields';
import { PERSON_KIND } from '../constants';

const submitButtonContent = (isLoading: boolean) =>
  isLoading ? <CircularProgress size="sm" /> : 'Confirmar';

const errorMessage = (hasError: boolean) => hasError && <ErrorMessage />;

const ClinicBillingForm = () => {
  const classes = useStyles();

  const {
    values,
    handleChange,
    touched,
    errors,
    setFieldValue,
    setFieldTouched,
  } = useFormikContext<ClinicBillingType>();

  const responsibleGridArea =
    values.person_kind === PERSON_KIND.PF ? 'responsible' : 'birthdate';

  const dispatch = useDispatch();
  const { isPending, hasError } = useSelector(requestStatusDerivedSelector);
  const { zipCodeIsLoading } = useGetZipCodeAddress();

  const documentMask = useRifm({
    value: values.cpf_cnpj,
    onChange: (value: string) => setFieldValue('cpf_cnpj', value),
    format:
      values.person_kind === PERSON_KIND.PJ ? cnpjFormatter : cpfFormatter,
  });

  const zipCodeMaskForms = useRifm({
    value: values.billing_zip,
    onChange: (value: string) => setFieldValue('billing_zip', value),
    format: cepFormatter,
  });

  useEffect(() => {
    const cleanCep = values.billing_zip?.replace(/\D/g, '');
    if (cleanCep && cleanCep.length === 8) {
      fetchZipCode(values.billing_zip).then((data) => {
        if (data && !data.error) {
          setFieldValue('billing_address', data.street ?? '');
          setFieldValue('billing_neighborhood', data.neighborhood ?? '');
          setFieldValue('billing_city', data.city ?? '');
          setFieldValue('billing_state', data.state ?? '');
        }
      });
    }
  }, [values.billing_zip, setFieldValue]);

  useEffect(() => {
    if (values.billing_zip.length === 9) {
      setFieldTouched('billing_zip', true);
    }
  }, [values.billing_zip, setFieldTouched]);

  const formDisabled = isPending ?? zipCodeIsLoading;

  return (
    <Form>
      <S.Root>
        <S.FieldsWrapper>
          <TextField
            label="Tipo de usuário"
            name="person_kind"
            value={values.person_kind}
            onChange={handleChange}
            select
            SelectProps={{
              native: true,
            }}
            style={{ gridArea: 'person_kind' }}
          >
            <option value={PERSON_KIND.PF}>Pessoa Física</option>
            <option value={PERSON_KIND.PJ}>Pessoa Jurídica</option>
          </TextField>

          <BillingAddressFields
            values={values}
            errors={errors}
            touched={touched}
            handleChange={handleChange}
            zipCodeValue={zipCodeMaskForms.value}
            zipCodeOnChange={zipCodeMaskForms.onChange}
          />

          <TextField
            id="cpf_cnpj"
            label={values.person_kind === PERSON_KIND.PJ ? 'CNPJ' : 'CPF'}
            name="cpf_cnpj"
            placeholder=""
            value={documentMask.value}
            onChange={documentMask.onChange}
            error={touched.cpf_cnpj && !!errors.cpf_cnpj}
            helperText={touched.cpf_cnpj && errors.cpf_cnpj}
            style={{ gridArea: 'document' }}
          />

          <ReactInputMask
            mask="99/99/9999"
            value={values.billing_birthdate ?? ''}
            onChange={handleChange}
            maskChar=""
          >
            {(inputProps: TextFieldProps) => (
              <TextField
                {...inputProps}
                id="billing_birthdate"
                data-testid="billing_birthdate"
                label="Data de Nascimento"
                name="billing_birthdate"
                error={touched.billing_birthdate && !!errors.billing_birthdate}
                helperText={
                  touched.billing_birthdate && errors.billing_birthdate
                }
                placeholder="DD/MM/AAAA"
                style={{
                  gridArea: 'birthdate',
                  display: values.person_kind !== PERSON_KIND.PF ? 'none' : '',
                }}
              />
            )}
          </ReactInputMask>

          <TextField
            id="responsible"
            label={
              values.person_kind === PERSON_KIND.PJ
                ? 'Razão Social'
                : 'Nome Completo'
            }
            name="responsible"
            value={values.responsible}
            onChange={handleChange}
            error={touched.responsible && !!errors.responsible}
            helperText={touched.responsible && errors.responsible}
            style={{ gridArea: responsibleGridArea }}
          />
        </S.FieldsWrapper>

        {errorMessage(hasError)}
        <S.Box>
          <S.Button
            color="transparent"
            className={classes.exitButton}
            onClick={() => dispatch(close())}
          >
            Cancelar
          </S.Button>
          <Button
            type="submit"
            disabled={formDisabled}
            className={classes.mainButton}
          >
            {submitButtonContent(formDisabled)}
          </Button>
        </S.Box>
      </S.Root>
    </Form>
  );
};

const ClinicBillingFormContainer = () => {
  const dispatch = useDispatch();
  const clinicBilling = useSelector(clinicBillingFormSelector);

  const initialValues: ClinicBillingType = {
    ...clinicBilling,
    person_kind:
      clinicBilling.person_kind === PERSON_KIND.PF
        ? PERSON_KIND.PF
        : PERSON_KIND.PJ,
    billing_country: 'BR',
    billing_birthdate: isoToBrDate(clinicBilling?.billing_birthdate),
  };

  if (!clinicBilling) return null;

  return (
    <Formik
      enableReinitialize
      initialValues={initialValues}
      onSubmit={(values) => {
        dispatch(
          submitClinicBilling({
            ...values,
            billing_country: 'BR',
          }),
        );
      }}
      validationSchema={validationClinicBillingForm}
    >
      <ClinicBillingForm />
    </Formik>
  );
};
export default ClinicBillingFormContainer;
